/**
 * Achat de billets — page publique, sans authentification.
 *
 * Chaque billet demande son propre porteur (prénom/nom/e-mail) : ce n'est pas
 * une lubie administrative, c'est ce qui permet à CHAQUE personne qui entre à
 * l'événement d'avoir son propre profil et ses propres suggestions de
 * rencontre — pas seulement l'acheteur. La billetterie est le premier pas du
 * moteur de matching, pas un simple encaissement.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { Marque } from '../composants/Marque.tsx';
import { Card, eur } from '../admin/ui.tsx';

type TypeBillet = {
  id: string;
  nom: string;
  description: string | null;
  prix_centimes: number;
  devise: string;
  places_restantes: number | null;
};

type EvenementPublic = {
  id: string;
  nom: string;
  description: string | null;
  lieu_nom: string | null;
  lieu_ville: string | null;
  debut_le: string;
  fin_le: string;
  organisation_nom: string;
};

type Porteur = { prenom: string; nom: string; email: string };

type BilletConfirme = {
  reference: string;
  typeNom: string;
  porteurPrenom: string | null;
  porteurNom: string | null;
  lienAcces: string | null;
  qrImage: string;
};

const dateLongue = (iso: string) =>
  new Date(iso).toLocaleDateString('fr-BE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

export function BilletterieAchat() {
  const { organisationSlug, evenementSlug } = useParams<{ organisationSlug: string; evenementSlug: string }>();

  const [evenement, setEvenement] = useState<EvenementPublic | null>(null);
  const [types, setTypes] = useState<TypeBillet[] | null>(null);
  const [chargement, setChargement] = useState(true);
  const [erreurChargement, setErreurChargement] = useState<string | null>(null);

  const [quantites, setQuantites] = useState<Record<string, number>>({});
  const [porteurs, setPorteurs] = useState<Record<string, Porteur[]>>({});
  const [acheteurEmail, setAcheteurEmail] = useState('');
  const [acheteurPrenom, setAcheteurPrenom] = useState('');
  const [acheteurNom, setAcheteurNom] = useState('');
  const [codePromo, setCodePromo] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<BilletConfirme[] | null>(null);

  useEffect(() => {
    if (!organisationSlug || !evenementSlug) return;
    fetch(`/api/billetterie/evenements/${organisationSlug}/${evenementSlug}`)
      .then(async (r) => {
        if (!r.ok) throw new Error((await r.json().catch(() => null))?.erreur?.message ?? `Erreur ${r.status}`);
        return r.json() as Promise<{ evenement: EvenementPublic; catalogue: TypeBillet[] }>;
      })
      .then((d) => {
        setEvenement(d.evenement);
        setTypes(d.catalogue);
      })
      .catch((e) => setErreurChargement(e instanceof Error ? e.message : 'Événement introuvable'))
      .finally(() => setChargement(false));
  }, [organisationSlug, evenementSlug]);

  const changerQuantite = (typeId: string, qte: number, max: number | null) => {
    const bornee = Math.max(0, Math.min(qte, max ?? 20, 20));
    setQuantites((q) => ({ ...q, [typeId]: bornee }));
    setPorteurs((p) => {
      const actuels = p[typeId] ?? [];
      const suivants = Array.from({ length: bornee }, (_, i) => actuels[i] ?? { prenom: '', nom: '', email: '' });
      return { ...p, [typeId]: suivants };
    });
  };

  const majPorteur = (typeId: string, index: number, champ: keyof Porteur, valeur: string) => {
    setPorteurs((p) => ({
      ...p,
      [typeId]: (p[typeId] ?? []).map((porteur, i) => (i === index ? { ...porteur, [champ]: valeur } : porteur)),
    }));
  };

  const totalUnites = Object.values(quantites).reduce((n, q) => n + q, 0);
  const sousTotalCentimes = (types ?? []).reduce((n, t) => n + (quantites[t.id] ?? 0) * t.prix_centimes, 0);

  const copierCoordonnees = (typeId: string, index: number) => {
    setPorteurs((p) => ({
      ...p,
      [typeId]: (p[typeId] ?? []).map((porteur, i) =>
        i === index ? { prenom: acheteurPrenom, nom: acheteurNom, email: acheteurEmail } : porteur,
      ),
    }));
  };

  const soumettre = async () => {
    setErreur(null);
    if (!acheteurEmail.trim()) {
      setErreur('Votre e-mail est requis');
      return;
    }
    if (totalUnites === 0) {
      setErreur('Choisissez au moins un billet');
      return;
    }
    const articles = Object.entries(quantites)
      .filter(([, q]) => q > 0)
      .map(([typeBilletId, q]) => ({
        typeBilletId,
        porteurs: (porteurs[typeBilletId] ?? []).slice(0, q).map((p) => ({
          prenom: p.prenom || undefined,
          nom: p.nom || undefined,
          email: p.email.trim(),
        })),
      }));
    if (articles.some((a) => a.porteurs.some((p) => !p.email))) {
      setErreur("Chaque billet doit avoir l'e-mail de son porteur");
      return;
    }

    setEnvoi(true);
    try {
      const reponse = await fetch(`/api/billetterie/evenements/${evenement!.id}/commandes`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          acheteurEmail: acheteurEmail.trim(),
          acheteurPrenom: acheteurPrenom || undefined,
          acheteurNom: acheteurNom || undefined,
          codePromo: codePromo.trim() || undefined,
          articles,
        }),
      });
      const donnees = await reponse.json();
      if (!reponse.ok) {
        setErreur(donnees?.erreur?.message ?? `Erreur ${reponse.status}`);
        return;
      }
      if (donnees.gratuite) {
        setConfirmation(donnees.billets);
      } else {
        window.location.assign(donnees.urlPaiement);
      }
    } catch {
      setErreur('Connexion indisponible — réessayez');
    } finally {
      setEnvoi(false);
    }
  };

  if (chargement) {
    return <PageBrute><p className="text-fumee">Chargement…</p></PageBrute>;
  }
  if (erreurChargement || !evenement || !types) {
    return (
      <PageBrute>
        <p className="text-fumee">{erreurChargement ?? 'Événement introuvable'}</p>
      </PageBrute>
    );
  }

  if (confirmation) {
    return (
      <PageBrute>
        <h1 className="display-title text-[28px] text-blanc sm:text-[36px]">
          C'est <em className="text-match-gradient font-serif italic font-medium">réservé</em>
        </h1>
        <p className="mt-3 text-[15px] text-fumee">
          {confirmation.length} billet(s) pour {evenement.nom}. Un e-mail avec votre QR d'entrée vient d'être envoyé
          à chaque porteur.
        </p>
        <div className="mt-6 flex flex-col gap-4">
          {confirmation.map((b) => (
            <Card key={b.reference} className="flex flex-wrap items-center gap-4">
              <img src={b.qrImage} alt={`QR du billet ${b.reference}`} className="h-24 w-24 rounded-[8px] bg-blanc p-1" />
              <div className="min-w-0 flex-1">
                <p className="text-[15px] text-blanc">{b.typeNom}</p>
                <p className="text-[13px] text-fumee">
                  {[b.porteurPrenom, b.porteurNom].filter(Boolean).join(' ') || 'Porteur à préciser'} — {b.reference}
                </p>
                {b.lienAcces && (
                  <a href={b.lienAcces} className="mt-1 inline-block text-[13px] text-blanc underline">
                    Ouvrir mon espace EventMatch
                  </a>
                )}
              </div>
            </Card>
          ))}
        </div>
      </PageBrute>
    );
  }

  return (
    <PageBrute>
      <h1 className="display-title text-[28px] text-blanc sm:text-[36px]">{evenement.nom}</h1>
      <p className="mt-2 text-[14px] text-fumee">
        {dateLongue(evenement.debut_le)}
        {evenement.lieu_nom ? ` · ${evenement.lieu_nom}` : ''}
        {evenement.lieu_ville ? `, ${evenement.lieu_ville}` : ''}
      </p>
      {evenement.description && <p className="mt-4 max-w-[620px] text-[15px] leading-relaxed text-fumee">{evenement.description}</p>}
      <p className="mt-2 text-[12px] uppercase tracking-[0.08em] text-fumee/70">
        Organisé par {evenement.organisation_nom} · billetterie EventMatch.pro
      </p>

      <div className="mt-8 flex flex-col gap-4">
        {types.length === 0 && <p className="text-fumee">Aucun billet n'est actuellement en vente.</p>}
        {types.map((t) => {
          const qte = quantites[t.id] ?? 0;
          const epuise = t.places_restantes !== null && t.places_restantes <= 0;
          return (
            <Card key={t.id}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-[16px] text-blanc">{t.nom}</p>
                  {t.description && <p className="mt-1 max-w-[420px] text-[13px] text-fumee">{t.description}</p>}
                  {t.places_restantes !== null && (
                    <p className="mt-1 text-[12px] text-fumee">
                      {epuise ? 'Épuisé' : `${t.places_restantes} place(s) restante(s)`}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-[16px] text-blanc">{t.prix_centimes === 0 ? 'Gratuit' : eur(t.prix_centimes / 100)}</span>
                  {!epuise ? (
                    <div className="flex items-center gap-2">
                      <button
                        className="h-8 w-8 rounded-full border border-graphite text-fumee hover:text-blanc"
                        onClick={() => changerQuantite(t.id, qte - 1, t.places_restantes)}
                        disabled={qte === 0}
                      >
                        −
                      </button>
                      <span className="w-6 text-center text-blanc">{qte}</span>
                      <button
                        className="h-8 w-8 rounded-full border border-graphite text-fumee hover:text-blanc"
                        onClick={() => changerQuantite(t.id, qte + 1, t.places_restantes)}
                      >
                        +
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>

              {qte > 0 && (
                <div className="mt-4 flex flex-col gap-3 border-t border-graphite pt-4">
                  {(porteurs[t.id] ?? []).map((p, i) => (
                    <div key={i} className="grid gap-2 sm:grid-cols-3">
                      <input
                        placeholder="Prénom du porteur"
                        value={p.prenom}
                        onChange={(e) => majPorteur(t.id, i, 'prenom', e.target.value)}
                        className="field-input !min-h-[38px] !py-1.5 !text-sm"
                      />
                      <input
                        placeholder="Nom du porteur"
                        value={p.nom}
                        onChange={(e) => majPorteur(t.id, i, 'nom', e.target.value)}
                        className="field-input !min-h-[38px] !py-1.5 !text-sm"
                      />
                      <div className="flex gap-2">
                        <input
                          placeholder="E-mail du porteur"
                          type="email"
                          value={p.email}
                          onChange={(e) => majPorteur(t.id, i, 'email', e.target.value)}
                          className="field-input !min-h-[38px] !py-1.5 !text-sm"
                        />
                        {totalUnites === 1 && acheteurEmail && (
                          <button
                            type="button"
                            onClick={() => copierCoordonnees(t.id, i)}
                            className="whitespace-nowrap text-[12px] text-fumee underline hover:text-blanc"
                          >
                            C'est moi
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {totalUnites > 0 && (
        <Card className="mt-6">
          <p className="label-caps text-fumee">Vos coordonnées</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <input
              placeholder="Votre e-mail"
              type="email"
              value={acheteurEmail}
              onChange={(e) => setAcheteurEmail(e.target.value)}
              className="field-input !min-h-[42px] !py-2 !text-sm"
            />
            <input
              placeholder="Votre prénom"
              value={acheteurPrenom}
              onChange={(e) => setAcheteurPrenom(e.target.value)}
              className="field-input !min-h-[42px] !py-2 !text-sm"
            />
            <input
              placeholder="Votre nom"
              value={acheteurNom}
              onChange={(e) => setAcheteurNom(e.target.value)}
              className="field-input !min-h-[42px] !py-2 !text-sm"
            />
          </div>

          <div className="mt-4 flex flex-wrap items-end gap-3">
            <label className="min-w-[180px]">
              <span className="label-caps block text-fumee">Code promo</span>
              <input
                value={codePromo}
                onChange={(e) => setCodePromo(e.target.value)}
                className="field-input mt-2 !min-h-[38px] !py-1.5 !text-sm"
                placeholder="Optionnel"
              />
            </label>
          </div>

          <div className="mt-5 flex flex-wrap items-center justify-between gap-4 border-t border-graphite pt-4">
            <p className="text-[15px] text-blanc">
              {totalUnites} billet(s) — {sousTotalCentimes === 0 ? 'Gratuit' : eur(sousTotalCentimes / 100)}
            </p>
            <button className="btn-creme" disabled={envoi} onClick={() => void soumettre()}>
              {envoi ? '…' : sousTotalCentimes === 0 ? 'Réserver' : 'Payer'}
            </button>
          </div>
          {erreur && (
            <p className="mt-3 text-[13px]" role="alert" style={{ color: 'var(--em-erreur)' }}>
              {erreur}
            </p>
          )}
        </Card>
      )}
    </PageBrute>
  );
}

function PageBrute({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-void px-4 py-10 font-sans text-blanc sm:px-8">
      <div className="mx-auto max-w-[720px]">
        <div className="mb-8 flex items-center gap-2.5">
          <Marque />
          <span className="text-[15px] font-medium text-blanc">
            EventMatch<span className="text-fumee">.pro</span>
          </span>
        </div>
        {children}
      </div>
    </div>
  );
}
